import { useState, useEffect, useCallback } from 'react';
import { X, ArrowRight, Loader2, CheckCircle, MapPin } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'acres_exit_popup_dismissed';
const DISMISS_DAYS = 7;

const ExitIntentPopup = ({ sourcePage = '' }) => {
  const [visible, setVisible] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', address: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const isDismissed = useCallback(() => {
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (!dismissed) return false;
    const expiry = parseInt(dismissed, 10);
    if (Date.now() > expiry) {
      localStorage.removeItem(STORAGE_KEY);
      return false;
    }
    return true;
  }, []);

  const dismiss = useCallback(() => {
    const expiry = Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(STORAGE_KEY, expiry.toString());
    setVisible(false);
  }, []);

  useEffect(() => {
    if (isDismissed()) return;
    let triggered = false;
    const handleMouseLeave = (e) => {
      if (e.clientY <= 5 && !triggered) {
        triggered = true;
        setVisible(true);
      }
    };
    // Delay attaching so we don't trigger immediately
    const timer = setTimeout(() => {
      document.addEventListener('mouseleave', handleMouseLeave);
    }, 8000);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isDismissed]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email) { setError('Please enter your email.'); return; }
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/forms/exit-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, source_page: sourcePage }),
      });
      if (res.ok) {
        setSubmitted(true);
        setTimeout(() => dismiss(), 4000);
      } else { setError('Something went wrong. Please try again.'); }
    } catch { setError('Network error. Please try again.'); }
    finally { setSubmitting(false); }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" data-testid="exit-intent-popup">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={dismiss} />

      {/* Modal */}
      <div className="relative bg-[#042E0C] max-w-lg w-full rounded-2xl overflow-hidden shadow-2xl">
        <button
          onClick={dismiss}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-white/40 hover:text-white transition-colors z-10"
          data-testid="exit-popup-close"
        >
          <X size={20} />
        </button>

        {/* Gold accent bar */}
        <div className="h-1 bg-gradient-to-r from-[#C4A36B] via-[#C4A36B]/60 to-transparent" />

        <div className="p-8 sm:p-10">
          {submitted ? (
            <div className="text-center py-4" data-testid="exit-popup-success">
              <CheckCircle size={48} className="mx-auto text-[#C4A36B] mb-4" />
              <h3 className="text-2xl font-heading font-light text-white mb-2">We'll Be in Touch</h3>
              <p className="text-white/60 text-sm">Our team will prepare your personalised property assessment and contact you within 48 hours.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-[#C4A36B]/20 flex items-center justify-center">
                  <MapPin size={20} className="text-[#C4A36B]" />
                </div>
                <p className="text-xs font-semibold tracking-[0.15em] uppercase text-[#C4A36B]">Before You Go</p>
              </div>

              <h2 className="font-heading text-2xl sm:text-3xl font-light text-white mb-2 leading-tight">
                Could your property be worth more than you think?
              </h2>
              <p className="text-white/50 text-sm mb-6">
                ACRES provides complimentary development potential assessments. No obligation, no cost — just the facts about what your property could be worth.
              </p>

              <form onSubmit={handleSubmit} className="space-y-3">
                <input
                  data-testid="exit-popup-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="Email address *"
                  className="w-full px-4 py-3 bg-white/10 border border-white/15 rounded-lg text-white placeholder:text-white/40 focus:outline-none focus:ring-1 focus:ring-[#C4A36B] text-sm"
                />
                <div className="grid grid-cols-2 gap-3">
                  <input
                    data-testid="exit-popup-name"
                    value={form.name}
                    onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="Name"
                    className="w-full px-4 py-3 bg-white/10 border border-white/15 rounded-lg text-white placeholder:text-white/40 focus:outline-none focus:ring-1 focus:ring-[#C4A36B] text-sm"
                  />
                  <input
                    data-testid="exit-popup-phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
                    placeholder="Phone"
                    className="w-full px-4 py-3 bg-white/10 border border-white/15 rounded-lg text-white placeholder:text-white/40 focus:outline-none focus:ring-1 focus:ring-[#C4A36B] text-sm"
                  />
                </div>
                <input
                  data-testid="exit-popup-address"
                  value={form.address}
                  onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))}
                  placeholder="Property address (optional)"
                  className="w-full px-4 py-3 bg-white/10 border border-white/15 rounded-lg text-white placeholder:text-white/40 focus:outline-none focus:ring-1 focus:ring-[#C4A36B] text-sm"
                />
                {error && <p className="text-red-400 text-sm">{error}</p>}
                <button
                  type="submit"
                  data-testid="exit-popup-submit"
                  disabled={submitting}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#C4A36B] text-[#042E0C] rounded-lg font-medium hover:bg-[#C4A36B]/90 transition-colors disabled:opacity-50 text-sm"
                >
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : <>Request Free Assessment <ArrowRight size={16} /></>}
                </button>
              </form>
              <p className="text-white/25 text-xs mt-3 text-center">No cost. No obligation. Unsubscribe anytime.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExitIntentPopup;
